import React, { useMemo, useState, useEffect } from "react";
import { AnalyticsData } from "../Constant";
import { SalesTrendsData } from "../store/merchantAnalyticsOverviewSlice";
import { ChartContainer } from "../styles";

interface SalesTrendChartProps {
  analyticsData: AnalyticsData;
  salesTrendsData?: SalesTrendsData | null;
  isDarkMode?: boolean;
  loading?: boolean;
}

const SalesTrendChart: React.FC<SalesTrendChartProps> = ({
  analyticsData,
  salesTrendsData,
  isDarkMode = false,
  loading = false,
}) => {
  const [ReactECharts, setReactECharts] = useState<any>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    // Dynamic import to avoid SSR issues
    import("echarts-for-react").then((module) => {
      setReactECharts(() => module.default);
    });
  }, []);

  const option = useMemo(() => {
    const salesDataPoints = salesTrendsData?.sales || [];
    const labels = salesDataPoints.map(item => {
      if (item.date.startsWith('Day ')) {
        return item.date;
      }
      try {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } catch {
        return item.date;
      }
    });
    const salesData = salesDataPoints.map(item => item.sales);

    return {
      tooltip: {
        trigger: "axis",
        backgroundColor: isDarkMode ? "#1a202c" : "#fff",
        borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
        textStyle: {
          color: isDarkMode ? "#fff" : "#1a202c",
        },
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          return `${param.name}<br/>${param.marker} Sales: $${param.value.toFixed(2)}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: isDarkMode ? "#4a5568" : "#d9d9d9",
          },
        },
        axisLabel: {
          color: isDarkMode ? "#a0aec0" : "#718096",
        },
      },
      yAxis: {
          type: "value",
          name: "Sales ($)",
          axisLine: {
            lineStyle: {
              color: isDarkMode ? "#4a5568" : "#d9d9d9",
            },
          },
          axisLabel: {
            color: isDarkMode ? "#a0aec0" : "#718096",
            formatter: (value: number) => `$${value.toLocaleString()}`,
          },
          splitLine: {
            lineStyle: {
              color: isDarkMode ? "#2d3748" : "#f0f0f0",
            },
          },
        },
      series: [
        {
          name: "Sales",
          type: "line",
          smooth: true,
          data: salesData,
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(49, 130, 206, 0.3)" },
                { offset: 1, color: "rgba(49, 130, 206, 0.05)" },
              ],
            },
          },
          lineStyle: {
            color: "#3182ce",
            width: 3,
          },
          itemStyle: {
            color: "#3182ce",
            borderWidth: 2,
            borderColor: isDarkMode ? "#1a202c" : "#fff",
          },
          emphasis: {
            focus: "series",
          },
        },
      ],
    };
  }, [isDarkMode, salesTrendsData]);

  if (!isMounted || !ReactECharts || loading) {
    return (
      <ChartContainer>
        <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {!isMounted || !ReactECharts ? "Loading chart..." : "Loading sales trends data..."}
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ReactECharts option={option} style={{ height: "100%", width: "100%" }} />
    </ChartContainer>
  );
};

export default SalesTrendChart;